import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, Chip, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, Grid, Skeleton, TextField, ToggleButton,
  ToggleButtonGroup, Typography, Alert, Snackbar,
} from '@mui/material';
import {
  Event as EventIcon, LocationOn, Add as AddIcon, DeleteOutline, People,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  apiListEvents, apiCreateEvent, apiRsvpEvent, apiCancelRsvp, apiDeleteEvent,
  type EventItem,
} from '../../services/api';
import { initials } from '../../utils/strings';

const SUPER_ADMIN_EMAIL = 'hl865@drexel.edu';
const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';

type Scope = 'upcoming' | 'past';

function formatEventWhen(startsAt: string, endsAt: string | null): string {
  const start = new Date(startsAt);
  if (Number.isNaN(start.getTime())) return '';
  const dateFmt = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
  let out = `${dateFmt.format(start)} · ${timeFmt.format(start)}`;
  if (endsAt) {
    const end = new Date(endsAt);
    if (!Number.isNaN(end.getTime())) {
      const sameDay = start.toDateString() === end.toDateString();
      out += sameDay ? ` – ${timeFmt.format(end)}` : ` – ${dateFmt.format(end)} · ${timeFmt.format(end)}`;
    }
  }
  return out;
}

// Convert a Date to the value an <input type="datetime-local"> expects (local time, no TZ).
function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const emptyForm = { title: '', startsAt: '', endsAt: '', location: '', description: '' };

const Events: React.FC = () => {
  const { user: me } = useAuth();
  const isSuperAdmin = String(me?.email || '').toLowerCase() === SUPER_ADMIN_EMAIL;
  const [scope, setScope] = useState<Scope>('upcoming');
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyRsvpId, setBusyRsvpId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [switching, setSwitching] = useState(false);
  const load = useCallback(async (which: Scope) => {
    // Keep the current list rendered (dimmed) while the other scope loads — clearing it
    // collapsed the page to a spinner and yanked the scroll position on every toggle.
    setError(null);
    setSwitching(true);
    try {
      const res = await apiListEvents(which, undefined, 50);
      setEvents(res.items ?? []);
    } catch {
      setEvents([]);
      setError('Could not load events.');
    } finally {
      setSwitching(false);
    }
  }, []);

  useEffect(() => { load(scope); }, [scope, load]);

  const patchEvent = (id: string, patch: Partial<EventItem>) =>
    setEvents((prev) => (prev ?? []).map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const handleRsvp = async (ev: EventItem, status: 'going' | 'interested') => {
    if (busyRsvpId) return;
    setBusyRsvpId(ev.id);
    const prevRsvp = ev.myRsvp;
    const counts = { ...ev.counts };
    try {
      if (prevRsvp === status) {
        // Toggle off.
        if (status === 'going') counts.going = Math.max(0, counts.going - 1);
        else counts.interested = Math.max(0, counts.interested - 1);
        patchEvent(ev.id, { myRsvp: null, counts });
        await apiCancelRsvp(ev.id);
      } else {
        if (prevRsvp === 'going') counts.going = Math.max(0, counts.going - 1);
        if (prevRsvp === 'interested') counts.interested = Math.max(0, counts.interested - 1);
        if (status === 'going') counts.going += 1; else counts.interested += 1;
        patchEvent(ev.id, { myRsvp: status, counts });
        await apiRsvpEvent(ev.id, status);
      }
    } catch {
      patchEvent(ev.id, { myRsvp: prevRsvp, counts: ev.counts });
      setToast('Could not update your RSVP.');
    } finally {
      setBusyRsvpId(null);
    }
  };

  const handleDelete = async (ev: EventItem) => {
    if (!window.confirm(`Delete "${ev.title}"? This can't be undone.`)) return;
    setDeletingId(ev.id);
    try {
      await apiDeleteEvent(ev.id);
      setEvents((prev) => (prev ?? []).filter((e) => e.id !== ev.id));
    } catch {
      setToast('Could not delete the event.');
    } finally {
      setDeletingId(null);
    }
  };

  const openCreate = () => {
    const inOneHour = new Date(Date.now() + 60 * 60 * 1000);
    setForm({ ...emptyForm, startsAt: toLocalInputValue(inOneHour) });
    setCreateError(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!form.title.trim() || !form.startsAt || creating) {
      setCreateError(!form.title.trim() ? 'Please add a title.' : 'Please pick a start time.');
      return;
    }
    const start = new Date(form.startsAt);
    if (Number.isNaN(start.getTime())) { setCreateError('Start time is invalid.'); return; }
    let endIso: string | undefined;
    if (form.endsAt) {
      const end = new Date(form.endsAt);
      if (Number.isNaN(end.getTime())) { setCreateError('End time is invalid.'); return; }
      if (end.getTime() < start.getTime()) { setCreateError('End time must be after the start time.'); return; }
      endIso = end.toISOString();
    }
    setCreating(true);
    setCreateError(null);
    try {
      const created = await apiCreateEvent({
        title: form.title.trim(),
        startsAt: start.toISOString(),
        endsAt: endIso ?? null,
        description: form.description.trim() || undefined,
        location: form.location.trim() || undefined,
      });
      setCreateOpen(false);
      if (scope === 'upcoming' && start.getTime() >= Date.now()) {
        setEvents((prev) => {
          const list = [...(prev ?? []), created];
          list.sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime());
          return list;
        });
      } else {
        setToast('Event created.');
      }
    } catch (e: any) {
      setCreateError(e?.message === 'validation_error' ? 'Please check the event details.' : 'Could not create the event. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    // disableGutters + py:1: Layout already wraps pages in a padded Container — doubling
    // the gutters made the content edge jump when switching tabs.
    <Container maxWidth="md" disableGutters sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Campus Events</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Discover what's happening on campus and let people know you're coming.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          Create event
        </Button>
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={scope}
        onChange={(_e, v) => { if (v) setScope(v); }}
        sx={{ my: 3 }}
      >
        <ToggleButton value="upcoming">Upcoming</ToggleButton>
        <ToggleButton value="past">Past</ToggleButton>
      </ToggleButtonGroup>

      {events === null ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={170} />)}
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : events.length === 0 ? (
        <Card><CardContent>
          <Typography color="text.secondary">
            {scope === 'upcoming' ? 'No upcoming events yet. Be the first to create one!' : 'No past events.'}
          </Typography>
        </CardContent></Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: switching ? 0.55 : 1, pointerEvents: switching ? 'none' : 'auto', transition: 'opacity .15s' }}>
          {events.map((ev) => {
            const canDelete = !!me?.id && (ev.creator?.id === me.id || isSuperAdmin);
            return (
              <Card key={ev.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main', flexGrow: 1, lineHeight: 1.25 }}>
                      {ev.title}
                    </Typography>
                    {canDelete && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline />}
                        onClick={() => handleDelete(ev)}
                        disabled={deletingId === ev.id}
                        sx={{ flexShrink: 0 }}
                      >
                        {deletingId === ev.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', mt: 0.5 }}>
                    <EventIcon fontSize="small" />
                    <Typography variant="body2">{formatEventWhen(ev.startsAt, ev.endsAt)}</Typography>
                  </Box>
                  {ev.location && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', mt: 0.5 }}>
                      <LocationOn fontSize="small" />
                      <Typography variant="body2">{ev.location}</Typography>
                    </Box>
                  )}

                  {ev.description && (
                    <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{ev.description}</Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    {ev.creator && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
                        <Avatar src={ev.creator.profileImage || undefined} sx={{ width: 26, height: 26, background: AVATAR_GRAD, color: '#fff', fontWeight: 700, fontSize: 12 }}>
                          {initials(ev.creator.displayName)}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">by {ev.creator.displayName}</Typography>
                      </Box>
                    )}
                    <Chip
                      size="small"
                      icon={<People sx={{ fontSize: 16 }} />}
                      label={`${ev.counts.going} going · ${ev.counts.interested} interested`}
                      variant="outlined"
                    />
                  </Box>

                  {scope === 'upcoming' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        size="small"
                        variant={ev.myRsvp === 'going' ? 'contained' : 'outlined'}
                        aria-pressed={ev.myRsvp === 'going'}
                        onClick={() => handleRsvp(ev, 'going')}
                        disabled={busyRsvpId === ev.id}
                      >
                        {ev.myRsvp === 'going' ? "I'm going ✓" : "I'm going"}
                      </Button>
                      <Button
                        size="small"
                        color="secondary"
                        variant={ev.myRsvp === 'interested' ? 'contained' : 'outlined'}
                        aria-pressed={ev.myRsvp === 'interested'}
                        onClick={() => handleRsvp(ev, 'interested')}
                        disabled={busyRsvpId === ev.id}
                      >
                        {ev.myRsvp === 'interested' ? 'Interested ✓' : 'Interested'}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Create event dialog */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create event</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="Title" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value.slice(0, 140) }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="datetime-local" label="Starts" InputLabelProps={{ shrink: true }}
                value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="datetime-local" label="Ends (optional)" InputLabelProps={{ shrink: true }}
                value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Location (optional)" value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value.slice(0, 200) }))} placeholder="e.g., Main Building, Room 302" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Description (optional)" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 2000) }))} />
            </Grid>
          </Grid>
          {createError && <Alert severity="error" sx={{ mt: 2 }}>{createError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={creating || !form.title.trim() || !form.startsAt}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 80 } }}
      >
        <Alert severity="info" variant="filled" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Container>
  );
};

export default Events;
